/* global dataLayer */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useForm, useFieldArray } from 'react-hook-form';
import { useStateMachine } from 'little-state-machine';
import { useRouter } from 'next/router';
import Fieldset from 'Components/Fieldset';
import FieldRow from 'Components/FieldRow';
import Field from 'Components/Field';
import ButtonAdd from 'Components/ButtonAdd';
import ButtonDelete from 'Components/ButtonDelete';
import ButtonNext from 'Components/ButtonNext';
import Textarea from 'Components/Textarea';
import Sheet from 'Components/Sheet';
import ImagePicker from 'Components/ImagePicker';
import updateAction from 'utils/updateAction';
import s from './Step1.module.scss';

const Step1 = ({ formNamespace, images }) => {
    const router = useRouter();
    const updateName = (state, payload) => {
        return updateAction(state, payload, formNamespace);
    };
    const { state, actions } = useStateMachine({ updateName });
    const {
        register,
        handleSubmit,
        getValues,
        setValue,
        formState,
        control,
        watch,
    } = useForm({
        mode: 'onChange',
        defaultValues: state[formNamespace],
    });
    const { errors, isValid } = formState;

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'persons',
    });
    const onSubmit = (data) => {
        dataLayer.push({
            event: 'checkoutSteps',
            eventCategory: 'hyllningsgåva',
            eventAction: 'hyllningsblad',
        });

        Object.assign(data, { steg1: true });
        actions.updateName(data);
        router.push({hash: 'steg2'});
    };

    watch();

    const { persons } = getValues();

    useEffect(() => {
        if (images.length === 1) {
            register('giftLayoutCode');
            setValue('giftLayoutCode', images[0].layoutCode);
        }
    }, []);

    return (
        <form className={s['Step1']} onSubmit={handleSubmit(onSubmit)}>
            <Fieldset title="Jag vill hylla">
                <Field
                    name="giftFirstName"
                    label="Förnamn"
                    required={true}
                    register={register('giftFirstName', {
                        required: 'Vänligen fyll i förnamn',
                    })}
                    errors={errors}
                />
                <Field
                    name="giftLastName"
                    label="Efternamn"
                    required={true}
                    register={register('giftLastName', {
                        required: 'Vänligen fyll i efternamn',
                    })}
                    errors={errors}
                />
            </Fieldset>
            {images && images.length > 1 &&
                <Fieldset title="Välj motiv">
                    <ImagePicker
                        images={images}
                        name="giftLayoutCode"
                        register={register('giftLayoutCode')}
                    />
                </Fieldset>
            }
            <Fieldset title="Hälsningstext">
                <Textarea
                    name="giftGreetingText"
                    placeholder="Hälsningstext (max 300 tecken)"
                    maxLength={300}
                    register={register('giftGreetingText')}
                    errors={errors}
                />
            </Fieldset>
            <Fieldset
                title="Hyllas av följande">
                {fields.map((field, index) => (
                    <div className={s['Step1__Familymember']} key={field.id}>
                        <FieldRow>
                            <Field
                                name={`persons[${index}].name`}
                                label="Namn"
                                required={true}
                                register={register(`persons[${index}].name`, {
                                    required: 'Vänligen fyll i ett namn',
                                })}
                                errors={errors}
                                error={errors?.persons?.[index]?.name ? true : false}
                                defaultValue={field.name}
                                width="Full"
                            />
                            <div className={s['Step1__ButtonDelete']}>
                                <ButtonDelete onClick={() => remove(index) }>
                                    Ta bort
                                </ButtonDelete>
                            </div>
                        </FieldRow>
                    </div>
                ))}
                <div className={s['Step1__ButtonAdd']}>
                    <ButtonAdd onClick={() => append({})}>
                        Lägg till person
                    </ButtonAdd>
                </div>
            </Fieldset>
            {images &&
                <Fieldset title="Förhandsgranska hyllningsbladet">
                    <Sheet
                        modifier="TributeGiftForm"
                        layoutCode={getValues('giftLayoutCode')}
                        images={images}
                        text="Hörselskadades Rikstförbund (HRF) har tacksamt mottagit en gåva för att hylla"
                        acknowledgement={getValues('giftGreetingText')}
                        firstName={getValues('giftFirstName')}
                        lastName={getValues('giftLastName')}
                        honoredBy={persons ? persons : []}
                        preview={true}
                    />
                </Fieldset>
            }
            <ButtonNext disabled={!isValid}>Nästa steg</ButtonNext>
        </form>
    );
};

Step1.propTypes = {
    formNamespace: PropTypes.string,
    images: PropTypes.any
};


export default Step1;
